import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { formatCurrency } from '../../services/voucherService';
import { API_BASE_URL } from '../../config/api.config';
import './VoucherBankTransfer.css';

interface BankDetails {
  bankName: string;
  iban: string;
  bic: string;
  accountHolder: string;
  reference: string;
}

const VoucherBankTransfer: React.FC = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') || 'GS-XXXX-XXXX';
  const amount = parseFloat(searchParams.get('amount') || '0');
  const voucherId = searchParams.get('voucherId');
  const tourReference = searchParams.get('fahrt'); // T-006: Fahrtbezug (informativ)

  const [bankDetails, setBankDetails] = useState<BankDetails>({
    bankName: '',
    iban: '',
    bic: '',
    accountHolder: '',
    reference: code, // T-050: Gutschein-Code traegt das Praefix GS- bereits
  });
  const [loading, setLoading] = useState(true);

  // Load bank details from settings API
  useEffect(() => {
    const fetchBankDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/systemsettings/public`);
        if (response.ok) {
          const data = await response.json();
          setBankDetails({
            bankName: data.bankName || '',
            iban: data.bankIban || '',
            bic: data.bankBic || '',
            accountHolder: data.companyName || '',
            reference: code, // T-050: Gutschein-Code traegt das Praefix GS- bereits
          });
        }
      } catch (error) {
        console.error('Error loading bank details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBankDetails();
  }, [code]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text.replace(/\s/g, ''));
    alert(`${label} wurde kopiert!`);
  };

  const handleDownloadInvoice = async () => {
    if (!voucherId) return;
    try {
      // T-026 (02.09.2026): anonymer Kaufabschluss weist sich ueber den Gutschein-Code aus
      const response = await fetch(`${API_BASE_URL}/vouchers/${voucherId}/invoice/pdf?code=${encodeURIComponent(code)}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Rechnung-${code}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Rechnung konnte nicht heruntergeladen werden.');
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Fehler beim Download der Rechnung.');
    }
  };

  return (
    <div className="voucher-bank-page">
      <div className="voucher-bank-container">
        <div className="bank-header">
          <div className="pending-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <h1>Gutschein reserviert!</h1>
          <p>
            Ihr Gutschein wurde erfolgreich reserviert und wird nach Zahlungseingang aktiviert.
          </p>
        </div>

        <div className="voucher-preview">
          <div className="preview-header">Ihr reservierter Gutschein</div>
          <div className="preview-body">
            <div className="preview-row">
              <span>Gutschein-Code:</span>
              <strong>{code}</strong>
            </div>
            <div className="preview-row">
              <span>Wert:</span>
              <strong>{formatCurrency(amount)}</strong>
            </div>
            {tourReference && (
              <div className="preview-row">
                <span>Gilt für:</span>
                <span>{tourReference}</span>
              </div>
            )}
            <div className="preview-row">
              <span>Status:</span>
              <span className="status-pending">Zahlung ausstehend</span>
            </div>
          </div>
        </div>

        <div className="bank-details-section">
          <h2>Überweisungsdaten</h2>
          <p className="bank-instruction">
            Bitte überweisen Sie den Betrag mit folgendem Verwendungszweck:
          </p>

          {loading ? (
            <div className="bank-details-card" style={{ textAlign: 'center', padding: '2rem' }}>
              <p>Überweisungsdaten werden geladen...</p>
            </div>
          ) : !bankDetails.iban ? (
            <div className="bank-details-card" style={{ textAlign: 'center', padding: '2rem', color: '#dc2626' }}>
              <p>Überweisungsdaten konnten nicht geladen werden. Bitte kontaktieren Sie uns.</p>
            </div>
          ) : (
          <div className="bank-details-card">
            <div className="bank-row">
              <span className="label">Empfänger:</span>
              <span className="value">{bankDetails.accountHolder}</span>
            </div>
            <div className="bank-row">
              <span className="label">Bank:</span>
              <span className="value">{bankDetails.bankName}</span>
            </div>
            <div className="bank-row clickable" onClick={() => copyToClipboard(bankDetails.iban, 'IBAN')}>
              <span className="label">IBAN:</span>
              <span className="value">{bankDetails.iban}</span>
              <button className="copy-btn" title="IBAN kopieren">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
              </button>
            </div>
            <div className="bank-row">
              <span className="label">BIC:</span>
              <span className="value">{bankDetails.bic}</span>
            </div>
            <div className="bank-row highlight">
              <span className="label">Betrag:</span>
              <span className="value">{formatCurrency(amount)}</span>
            </div>
            <div className="bank-row highlight clickable" onClick={() => copyToClipboard(bankDetails.reference, 'Verwendungszweck')}>
              <span className="label">Verwendungszweck:</span>
              <span className="value">{bankDetails.reference}</span>
              <button className="copy-btn" title="Verwendungszweck kopieren">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
              </button>
            </div>
          </div>
          )}

          {bankDetails.iban && (
            <div className="important-note">
              <strong>Wichtig:</strong> Bitte geben Sie unbedingt den Verwendungszweck{' '}
              <code>{bankDetails.reference}</code> an, damit wir die Zahlung zuordnen können.
            </div>
          )}
        </div>

        <div className="next-steps">
          <h3>Nächste Schritte</h3>
          <ol>
            <li>
              <span className="step-number">1</span>
              <span>Überweisen Sie den Betrag mit dem angegebenen Verwendungszweck.</span>
            </li>
            <li>
              <span className="step-number">2</span>
              <span>Nach Zahlungseingang (1-3 Werktage) wird Ihr Gutschein automatisch aktiviert.</span>
            </li>
            <li>
              <span className="step-number">3</span>
              <span>Sie erhalten eine E-Mail mit dem PDF-Gutschein zum Ausdrucken.</span>
            </li>
          </ol>

          {voucherId && (
            <div className="invoice-download" style={{ marginTop: '1.5rem', textAlign: 'center' }}>
              <button
                onClick={handleDownloadInvoice}
                className="btn-secondary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Rechnung herunterladen
              </button>
            </div>
          )}
        </div>

        <div className="bank-actions">
          <Link to="/gutschein-kaufen" className="btn-secondary">
            Weiteren Gutschein kaufen
          </Link>
          <Link to="/" className="btn-primary">
            Zur Startseite
          </Link>
        </div>

        <p className="bank-footer">
          Sie haben die Überweisungsdaten auch per E-Mail erhalten.
          Bei Fragen wenden Sie sich an: <a href="mailto:info@frankenfloss.de">info@frankenfloss.de</a>
        </p>
      </div>
    </div>
  );
};

export default VoucherBankTransfer;
